import { z } from 'zod';
import { DEFAULT_PRINTER_DISCOVERY_SDKS, zPaymentType, zPrinterSdk } from '../enums';
import {
  docketTemplateCvxSchema,
  docketTemplateSchema,
  docketTemplateStrictSchema,
} from './docketTemplateSchema';
import { posPasscodeSchema, posPasscodeStrictSchema } from './posPasscodeSchema';
import { posPrinterCvxSchema, posPrinterSchema, posPrinterStrictSchema } from './posPrinterSchema';
import { posTerminalCvxSchema, posTerminalSchema } from './posTerminalSchema';

export const posConfigSchema = z.object({
  autoAcceptEstWaitTime: z.number().nullable().default(null),
  docketTemplates: z.array(docketTemplateSchema).default([]),
  openCashDrawerTriggers: z.array(zPaymentType).default([]),
  posPrinters: z.array(posPrinterSchema).default([]),
  /**
   * Which native printer SDK(s) the POS app searches during printer discovery.
   * Discovery always runs sequentially in `PRINTER_SDKS` order (Star Micronics, then Epson),
   * regardless of the order stored here. An empty array disables discovery entirely.
   */
  printerDiscoverySdks: z
    .array(zPrinterSdk)
    .default([...DEFAULT_PRINTER_DISCOVERY_SDKS])
    .optional(),
  printJobLoggingEnabled: z.boolean().default(false),
  restaurantId: z.string(),

  /**
   * @deprecated Replaced by the iPads table; kept so existing posConfig docs validate.
   * Remove after data is migrated.
   */
  posTerminals: z.array(posTerminalSchema).optional(),

  posPasscodes: z.array(posPasscodeSchema).default([]).optional(),

  _creationTime: z.number().optional(),
  _id: z.string().optional(),
  updatedAt: z.number().optional(),
});
export type PosConfig = z.output<typeof posConfigSchema>;

export const posConfigStrictSchema = posConfigSchema
  .safeExtend({
    docketTemplates: z.array(docketTemplateStrictSchema),
    posPrinters: z.array(posPrinterStrictSchema),
    posPasscodes: z.array(posPasscodeStrictSchema).optional(),
  })
  /** Validate: A USB printer can only be attached to 1 iPad (each iPad has ≤1 USB printer). */
  .refine(
    (config) => {
      const attachedIPadUuids = config.posPrinters
        .map((printer) => printer.attachedToIPadUuid)
        .filter((uuid): uuid is string => uuid !== null);

      return new Set(attachedIPadUuids).size === attachedIPadUuids.length;
    },
    {
      message: 'Invalid POS configuration: A single iPad has multiple USB printers attached.',
      path: ['posPrinters', 'attachedToIPadUuid'],
    },
  )
  /** Validate: No duplicate passcodes allowed */
  .refine(
    (config) => {
      if (!config.posPasscodes || config.posPasscodes.length === 0) return true;
      const passcodes = config.posPasscodes.map((p) => p.passcode);
      return new Set(passcodes).size === passcodes.length;
    },
    {
      message: 'Duplicate passcodes are not allowed',
      path: ['posPasscodes'],
    },
  )
  /** Validate: At least one MANAGER required if passcodes exist */
  .refine(
    (config) => {
      if (!config.posPasscodes || config.posPasscodes.length === 0) return true;
      return config.posPasscodes.some((p) => p.role === 'MANAGER');
    },
    {
      message: 'At least one MANAGER passcode is required',
      path: ['posPasscodes'],
    },
  );

export const posConfigWithoutRestaurantIdSchema = posConfigSchema.omit({ restaurantId: true });
export type PosConfigWithoutRestaurantId = z.output<typeof posConfigWithoutRestaurantIdSchema>;

export const posConfigCvxSchema = posConfigSchema.safeExtend({
  _creationTime: z.number(),
  _id: z.string(),
  docketTemplates: z.array(docketTemplateCvxSchema).default([]),
  posPrinters: z.array(posPrinterCvxSchema).default([]),
  posTerminals: z.array(posTerminalCvxSchema).optional(),
  updatedAt: z.number(),
});
export type PosConfigCvx = z.output<typeof posConfigCvxSchema>;
